/**
 * Cart Custom Fee Manager - 购物车定制费管理器
 * 主要的购物车定制费监控和处理逻辑
 */

class CartCustomFeeManager {
  constructor() {
    // 防止重复创建实例
    if (window.cartCustomFeeManager && window.cartCustomFeeManager instanceof CartCustomFeeManager) {
      return window.cartCustomFeeManager;
    }
    
    // 检查是否开启调试模式
    this.isDebugMode = this.checkDebugMode();
    this.init();
  }

  /**
   * 检查调试模式：URL参数 ?dev=1 或 localStorage
   */
  checkDebugMode() {
    // 首先检查URL参数
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get('dev') === '1') {
      // 如果URL有dev=1，保存到localStorage
      localStorage.setItem('cart_custom_fee_debug', 'true');
      return true;
    }
    
    // 然后检查localStorage
    return localStorage.getItem('cart_custom_fee_debug') === 'true';
  }

  /**
   * 调试日志输出
   */
  debugLog(...args) {
    if (this.isDebugMode) {
      console.log(...args);
      
      // 在日志最后添加关闭调试的提示
      if (args[0] && args[0].includes('📦')) {
        console.log('💡 关闭调试模式: localStorage.removeItem("cart_custom_fee_debug")');
      }
    }
  }

  init() {
    // 防止重复初始化
    if (this.initialized) {
      return;
    }
    
    // 只在购物车页面初始化，避免在其他页面显示不必要的loading
    if (window.location.pathname !== '/cart') {
      this.debugLog('🚫 非购物车页面，跳过Cart Custom Fee Manager初始化');
      return;
    }
    
    this.debugLog('🔍 Cart Custom Fee Manager 已初始化');
    this.initialized = true;
    
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => {
        this.getCartData();
      });
    } else {
      this.getCartData();
    }
  }

  /**
   * 获取购物车原始数据并处理定制费
   * @param {boolean} autoRefresh - 是否在有变化时自动刷新页面，默认为true
   */
  async getCartData(autoRefresh = true) {
    this.debugLog('📊 获取购物车原始数据', `自动刷新=${autoRefresh}`);
    
    try {
      // 获取购物车JSON数据
      const response = await fetch('/cart.js');
      const cartData = await response.json();
      
      this.debugLog('🛒 购物车原始数据:', cartData);
      
      // 如果有商品，先检查是否包含定制商品
      if (cartData.items && cartData.items.length > 0) {
        this.debugLog('📦 购物车商品列表:', cartData.items);
        
        // 快速检查是否有定制商品
        const hasCustomProducts = cartData.items.some(item => this.isCustomProduct(item));
        
        if (!hasCustomProducts) {
          this.debugLog('ℹ️ 购物车中没有定制商品，跳过定制费处理');
          return; // 没有定制商品，直接返回，不显示loading
        }
        
        // 有定制商品时才显示处理提示
        this.showProcessingNotification();
        
        // 分析并处理定制商品
        await this.processCustomProducts(cartData.items, autoRefresh);
      } else {
        this.debugLog('🛒 购物车为空');
        // 购物车为空，不需要显示loading
      }
      
    } catch (error) {
      this.debugLog('❌ 获取购物车数据失败:', error);
      this.hideProcessingNotification(); // 出错时隐藏可能已显示的提示
    }
  }

  /**
   * 处理定制商品，自动添加对应的定制费
   * @param {Array} cartItems - 购物车商品列表
   * @param {boolean} autoRefresh - 是否在有变化时自动刷新页面，默认为true
   */
  async processCustomProducts(cartItems, autoRefresh = true) {
    this.debugLog('🎯 开始处理定制商品', `自动刷新=${autoRefresh}`);
    
    // 分离定制商品和定制费商品
    const customProducts = [];
    const existingFees = [];
    
    cartItems.forEach(item => {
      // 检查是否是定制商品
      if (this.isCustomProduct(item)) {
        customProducts.push(item);
      }
      // 检查是否是定制费商品
      else if (this.isCustomFeeProduct(item)) {
        existingFees.push(item);
      }
    });
    
    this.debugLog(`🔍 找到 ${customProducts.length} 个定制商品, ${existingFees.length} 个定制费商品`);
    console.log('[Manager] 现有费用商品:', existingFees);
    
    // 如果没有定制商品，直接返回，不需要处理
    if (customProducts.length === 0) {
      this.debugLog('✅ 没有定制商品，无需处理定制费');
      return;
    }
    
    // 批量计算所有需要的定制费
    const requiredFees = await this.calculateAllRequiredFees(customProducts);
    console.log('[Manager] 需要的费用项:', requiredFees);
    this.debugLog('📋 计算出的所有需要的定制费:', requiredFees);
    
    // 比较现有费用和需要的费用，确定需要执行的操作
    const operations = this.planFeeOperations(existingFees, requiredFees);
    console.log('[Manager] 计划执行的操作:', operations);
    this.debugLog('🔄 计划执行的操作:', operations);
    
    // 如果没有操作需要执行，直接返回
    if (operations.toRemove.length === 0 && operations.toAdd.length === 0) {
      console.log('[Manager] ✅ 购物车定制费已正确，无需变更');
      this.debugLog('✅ 购物车定制费已正确，无需变更');
      this.hideProcessingNotification(); // 隐藏提示
      return;
    }
    
    // 更新提示信息
    this.updateProcessingNotification('Updating custom fees...');
    
    // 批量执行所有操作
    console.log('[Manager] 🚀 开始执行购物车操作...');
    await this.executeFeeOperations(operations);
    
    // 根据autoRefresh参数决定是否自动刷新页面
    if (autoRefresh) {
      this.debugLog('🔄 检测到定制费变化，自动刷新页面...');
      this.updateProcessingNotification('Custom fees updated, refreshing page...');
      setTimeout(() => {
        window.location.reload();
      }, 1000);
    } else {
      this.debugLog('✅ 所有定制费处理完成（由拦截器控制后续跳转）');
      this.hideProcessingNotification();
    }
  }

  /**
   * 显示处理中提示
   */
  showProcessingNotification() {
    // 避免重复创建
    if (document.getElementById('cart-fee-processing-notification')) {
      return;
    }
    
    const notification = document.createElement('div');
    notification.id = 'cart-fee-processing-notification';
    notification.style.cssText = `
      position: fixed;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background: rgba(0, 0, 0, 0.5);
      display: flex;
      align-items: center;
      justify-content: center;
      z-index: 10000;
      opacity: 0;
      transition: opacity 0.3s ease;
    `;
    
    notification.innerHTML = `
      <div style="
        background: white;
        border-radius: 8px;
        padding: 24px 32px;
        box-shadow: 0 4px 20px rgba(0,0,0,0.15);
        display: flex;
        align-items: center;
        gap: 16px;
        font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
        font-size: 16px;
        color: #333;
        min-width: 280px;
      ">
        <div style="
          width: 24px; 
          height: 24px; 
          border: 3px solid #e3e3e3; 
          border-top: 3px solid #333; 
          border-radius: 50%; 
          animation: spin 1s linear infinite;
        "></div>
        <span>Calculating custom fees, please wait...</span>
      </div>
      <style>
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      </style>
    `;
    
    document.body.appendChild(notification);
    
    // 动画显示
    setTimeout(() => {
      notification.style.opacity = '1';
    }, 10);
  }

  /**
   * 更新处理提示文本
   */
  updateProcessingNotification(message) {
    const notification = document.getElementById('cart-fee-processing-notification');
    if (notification) {
      const textSpan = notification.querySelector('span');
      if (textSpan) {
        textSpan.textContent = message;
      }
    }
  }

  /**
   * 隐藏处理提示
   */
  hideProcessingNotification() {
    const notification = document.getElementById('cart-fee-processing-notification');
    if (notification) {
      notification.style.opacity = '0';
      setTimeout(() => {
        notification.remove();
      }, 300);
    }
  }

  /**
   * 检查是否是定制商品
   */
  isCustomProduct(item) {
    return item.properties && item.properties._is_custom_product === 'true';
  }

  /**
   * 检查是否是定制费商品
   */
  isCustomFeeProduct(item) {
    // 检查 Setup Fee
    const setupFeeVariantId = SETUP_FEE_CONFIG.variantId;
    if (item.variant_id === setupFeeVariantId) {
      return true;
    }
    
    if (item.properties && item.properties._setup_fee === 'true') {
      return true;
    }
    
    // 检查所有已注册的工艺类型
    const processTypes = CraftProcessorFactory.getAllProcessTypes();
    for (const processType of processTypes) {
      try {
        const processor = CraftProcessorFactory.getProcessor(processType);
        if (processor.isCustomFeeProduct(item)) {
          return true;
        }
      } catch (error) {
        this.debugLog(`⚠️ 检查工艺类型 ${processType} 时出错:`, error);
        continue;
      }
    }
    
    return false;
  }

  /**
   * 批量计算所有需要的定制费
   */
  async calculateAllRequiredFees(customProducts) {
    this.debugLog('🧮 开始批量计算所有需要的定制费');
    
    // 按工艺类型分组
    const productsByType = this.groupProductsByType(customProducts);
    this.debugLog('📋 按工艺类型分组的商品:', productsByType);
    
    // 为每种工艺类型委托给对应处理器计算
    const allRequiredFees = [];
    
    for (const [craftType, products] of Object.entries(productsByType)) {
      try {
        this.debugLog(`🔄 委托给工艺处理器计算: ${craftType}, 商品数量: ${products.length}`);
        
        // 获取工艺处理器
        const processor = CraftProcessorFactory.getProcessor(craftType);
        
        // 委托给处理器计算，处理器负责所有私有逻辑
        const feeItems = processor.calculateRequiredFeeItems(products);
        console.log(`[Manager] 工艺 ${craftType} 返回 ${feeItems.length} 个费用项:`, feeItems);
        this.debugLog(`✅ 工艺 ${craftType} 返回 ${feeItems.length} 个费用项`);
        
        // 为费用项添加标准化属性用于后续比较
        const standardizedItems = this.standardizeFeeItems(feeItems);
        console.log(`[Manager] 标准化后的费用项:`, standardizedItems);
        allRequiredFees.push(...standardizedItems);
        
      } catch (error) {
        this.debugLog(`❌ 处理工艺类型 ${craftType} 时出错:`, error);
        continue;
      }
    }
    
    console.log(`[Manager] 总共收集到 ${allRequiredFees.length} 个费用项:`, allRequiredFees);
    this.debugLog(`📊 总共收集到 ${allRequiredFees.length} 个费用项`);
    return allRequiredFees;
  }

  /**
   * 按工艺类型分组商品（纯数据转换，无业务逻辑）
   */
  groupProductsByType(customProducts) {
    const productsByType = {};
    
    console.log('[Manager] 🔍 开始按工艺类型分组商品', {
      totalProducts: customProducts.length,
      products: customProducts.map(p => ({
        title: p.title,
        craftType: p.properties._internal_craft_type,
        groupId: p.properties._group_id,
        isCustom: p.properties._is_custom_product,
        allProperties: Object.keys(p.properties || {})
      }))
    });
    
    for (const product of customProducts) {
      const craftType = product.properties._internal_craft_type;
      const draftOrderId = product.properties._draft_order;
      const groupId = product.properties._group_id; // 新的分组标识
      
      console.log('[Manager] 📋 处理商品', {
        title: product.title,
        craftType,
        groupId,
        draftOrderId,
        hasProperties: !!product.properties,
        properties: product.properties
      });
      
      // 基础验证
      if (!craftType) {
        console.warn(`[Manager] ⚠️ 商品缺少工艺类型，跳过: ${product.title}`, {
          properties: product.properties
        });
        this.debugLog(`⚠️ 商品缺少工艺类型，跳过: ${product.title}`);
        continue;
      }
      
      if (!groupId) {
        console.warn(`[Manager] ⚠️ 商品缺少group_id，跳过: ${product.title}`, {
          craftType,
          properties: product.properties
        });
        this.debugLog(`⚠️ 商品缺少group_id，跳过: ${product.title}`);
        continue;
      }
      
      // 解析定制面信息（通用解析，不涉及具体工艺逻辑）
      let customSides = [];
      try {
        // 优先使用 _custom_sides_internal（JSON格式）
        const customSidesInternal = product.properties['_custom_sides_internal'];
        const customSidesPublic = product.properties['Custom Sides'];
        
        console.log(`[Manager] 🔍 解析定制面 - Internal: ${customSidesInternal}, Public: ${customSidesPublic}`);
        
        if (customSidesInternal) {
          customSides = JSON.parse(customSidesInternal);
          console.log(`[Manager] ✅ 使用Internal格式解析:`, customSides);
        } 
        // 备选方案：使用 Custom Sides（逗号分隔字符串）
        else {
          if (customSidesPublic) {
            // 如果是JSON格式，尝试解析
            if (customSidesPublic.startsWith('[') && customSidesPublic.endsWith(']')) {
              customSides = JSON.parse(customSidesPublic);
              console.log(`[Manager] ✅ 使用Public JSON格式解析:`, customSides);
            } 
            // 如果是逗号分隔字符串，直接分割
            else if (customSidesPublic.includes(',')) {
              customSides = customSidesPublic.split(',').map(side => side.trim());
              console.log(`[Manager] ✅ 使用逗号分隔格式解析:`, customSides);
            }
            // 单个定制面
            else {
              customSides = [customSidesPublic.trim()];
              console.log(`[Manager] ✅ 使用单一面格式解析:`, customSides);
            }
          }
        }
        
        // 转换面名称为统一的小写格式供内部使用
        customSides = customSides.map(side => side.toLowerCase());
        console.log(`[Manager] 📝 最终定制面列表:`, customSides);
        
      } catch (error) {
        console.log(`[Manager] ⚠️ 解析定制面信息失败:`, error);
        this.debugLog(`⚠️ 解析定制面信息失败，使用默认值: ${product.title}`, error);
        customSides = ['front']; // 默认前面
      }
      
      this.debugLog(`📝 收集商品: ${product.title}, 工艺: ${craftType}, 数量: ${product.quantity}, 定制面: ${JSON.stringify(customSides)}, group: ${groupId}, 草稿单: ${draftOrderId}`);
      
      // 分组
      if (!productsByType[craftType]) {
        productsByType[craftType] = [];
      }
      
      productsByType[craftType].push({
        quantity: product.quantity,
        customSides,
        draftOrderId, // 保留草稿单ID用于兼容
        groupId, // 新的主要分组标识
        product: product // 保留完整的product对象供处理器使用
      });
    }
    
    console.log('[Manager] 🎯 最终分组结果', {
      craftTypes: Object.keys(productsByType),
      details: Object.entries(productsByType).map(([type, products]) => ({
        craftType: type,
        productCount: products.length,
        totalQuantity: products.reduce((sum, p) => sum + p.quantity, 0),
        groups: [...new Set(products.map(p => p.groupId))]
      }))
    });
    
    return productsByType;
  }

  /**
   * 标准化费用项（添加Manager需要的通用属性）
   */
  standardizeFeeItems(feeItems) {
    return feeItems.map(item => {
      // 获取分组标识（优先使用group_id）
      const groupId = item.properties._group_id || item.properties._draft_order_id;
      
      // 为每个费用项添加唯一标识key用于比较
      if (item.properties._setup_fee === 'true') {
        item.key = `setup_${groupId}`;
      } else if (item.properties._custom_side) {
        item.key = `custom_${groupId}_${item.properties._custom_side}`;
      } else {
        item.key = `unknown_${Date.now()}_${Math.random()}`;
        this.debugLog(`⚠️ 费用项缺少标识信息，生成随机key: ${item.key}`);
      }
      
      // 标准化variantId属性
      item.variantId = item.id;
      
      return item;
    });
  }

  /**
   * 规划费用操作（删除和添加）
   */
  planFeeOperations(existingFees, requiredFees) {
    const operations = {
      toRemove: [],
      toAdd: []
    };
    
    // 为现有费用创建比较键
    const existingFeeMap = new Map();
    existingFees.forEach(fee => {
      // 优先使用group_id，兼容旧的draft_order_id
      const groupId = fee.properties._group_id || fee.properties._draft_order_id || fee.properties._draft_order;
      if (!fee.properties || !groupId) return;
      
      let key;
      if (fee.properties._setup_fee === 'true') {
        key = `setup_${groupId}`;
      } else if (fee.properties._custom_side) {
        key = `custom_${groupId}_${fee.properties._custom_side}`;
      } else {
        return; // 无法识别的费用类型
      }
      
      existingFeeMap.set(key, {
        fee,
        variantId: fee.variant_id,
        quantity: fee.quantity,
        matched: false
      });
    });
    
    // 检查需要的费用
    for (const required of requiredFees) {
      console.log('[Manager] 检查需要的费用项:', required);
      const existing = existingFeeMap.get(required.key);
      console.log('[Manager] 现有匹配的费用:', existing);
      
      if (existing) {
        // 检查是否匹配
        if (existing.variantId === required.variantId && existing.quantity === required.quantity) {
          existing.matched = true; // 标记为匹配，不需要操作
          this.debugLog(`✅ 费用已正确: ${required.key}`);
          console.log(`[Manager] ✅ 费用已正确: ${required.key}`);
        } else {
          // 需要更新：先删除旧的，再添加新的
          operations.toRemove.push(existing.fee);
          operations.toAdd.push(required);
          existing.matched = true; // 避免重复删除
          this.debugLog(`🔄 费用需要更新: ${required.key}, 旧变体${existing.variantId}→新变体${required.variantId}, 旧数量${existing.quantity}→新数量${required.quantity}`);
          console.log(`[Manager] 🔄 费用需要更新: ${required.key}`);
        }
      } else {
        // 不存在，需要添加
        operations.toAdd.push(required);
        this.debugLog(`➕ 需要添加费用: ${required.key}`);
        console.log(`[Manager] ➕ 需要添加费用: ${required.key}`);
      }
    }
    
    // 删除不再需要的费用
    for (const [key, existing] of existingFeeMap) {
      if (!existing.matched) {
        operations.toRemove.push(existing.fee);
        this.debugLog(`🗑️ 需要删除费用: ${key}`);
      }
    }
    
    return operations;
  }

  /**
   * 执行费用操作（批量优化版本）
   */
  async executeFeeOperations(operations) {
    this.debugLog(`📊 开始批量执行操作: 删除${operations.toRemove.length}个, 添加${operations.toAdd.length}个`);
    
    // 1. 批量删除（通过设置数量为0）
    if (operations.toRemove.length > 0) {
      await this.batchRemoveFromCart(operations.toRemove);
    }
    
    // 2. 批量添加
    if (operations.toAdd.length > 0) {
      await this.batchAddToCart(operations.toAdd);
    }
    
    this.debugLog(`📊 批量操作完成: 删除${operations.toRemove.length}个, 添加${operations.toAdd.length}个`);
  }

  /**
   * 批量删除商品（通过设置数量为0）
   */
  async batchRemoveFromCart(feesToRemove) {
    this.debugLog(`🗑️ 批量删除 ${feesToRemove.length} 个费用商品`);
    
    try {
      // 构建批量更新的数据结构
      const updates = {};
      feesToRemove.forEach(fee => {
        updates[fee.key] = 0; // 设置数量为0表示删除
      });
      
      this.debugLog(`📤 批量删除请求数据:`, updates);
      
      const response = await fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ updates: updates })
      });
      
      if (response.ok) {
        this.debugLog(`✅ 批量删除成功: ${feesToRemove.length} 个商品`);
        return true;
      } else {
        const errorText = await response.text();
        this.debugLog(`❌ 批量删除失败: ${response.status} - ${errorText}`);
        return false;
      }
    } catch (error) {
      this.debugLog(`❌ 批量删除出错:`, error);
      return false;
    }
  }

  /**
   * 批量添加商品到购物车
   */
  async batchAddToCart(feesToAdd) {
    this.debugLog(`➕ 批量添加 ${feesToAdd.length} 个费用商品`);
    
    try {
      // 构建批量添加的数据结构
      const items = feesToAdd.map(fee => ({
        id: fee.variantId,
        quantity: fee.quantity,
        properties: fee.properties
      }));
      
      this.debugLog(`📤 批量添加请求数据:`, items);
      
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items: items })
      });
      
      if (response.ok) {
        this.debugLog(`✅ 批量添加成功: ${feesToAdd.length} 个商品`);
        return true;
      } else {
        const errorText = await response.text();
        this.debugLog(`❌ 批量添加失败: ${response.status} - ${errorText}`);
        
        // 如果批量添加失败，回退到单个添加
        this.debugLog(`🔄 回退到单个添加模式`);
        return await this.fallbackSingleAdd(feesToAdd);
      }
    } catch (error) {
      this.debugLog(`❌ 批量添加出错:`, error);
      // 如果批量添加失败，回退到单个添加
      this.debugLog(`🔄 回退到单个添加模式`);
      return await this.fallbackSingleAdd(feesToAdd);
    }
  }

  /**
   * 回退到单个添加（当批量添加失败时）
   */
  async fallbackSingleAdd(feesToAdd) {
    let successCount = 0;
    
    for (const fee of feesToAdd) {
      const success = await this.addToCartWithProperties(fee.variantId, fee.quantity, fee.properties);
      if (success) {
        successCount++;
      }
    }
    
    this.debugLog(`🔄 单个添加完成: ${successCount}/${feesToAdd.length} 成功`);
    return successCount === feesToAdd.length;
  }

  /**
   * 从购物车删除商品（通过key精确删除）- 保留作为备用方法
   */
  async removeFromCartByKey(itemKey) {
    this.debugLog(`🗑️ 删除购物车商品（按key): ${itemKey}`);
    try {
      const response = await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          id: itemKey,
          quantity: 0
        })
      });
      
      if (response.ok) {
        this.debugLog(`✅ 成功删除商品（按key): ${itemKey}`);
      } else {
        this.debugLog(`❌ 删除商品失败（按key): ${itemKey}`);
      }
    } catch (error) {
      this.debugLog(`❌ 删除商品出错（按key): ${itemKey}`, error);
    }
  }

  /**
   * 添加商品到购物车（带属性）- 保留作为备用方法
   */
  async addToCartWithProperties(variantId, quantity, properties = {}) {
    this.debugLog(`➕ 添加到购物车（带属性): 变体${variantId}, 数量${quantity}, 属性:`, properties);
    try {
      const requestBody = {
        items: [{
          id: variantId,
          quantity: quantity,
          properties: properties
        }]
      };
      
      this.debugLog(`📤 发送请求到 /cart/add.js:`, requestBody);
      
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(requestBody)
      });
      
      const responseText = await response.text();
      this.debugLog(`📥 响应状态: ${response.status}, 响应内容:`, responseText);
      
      if (response.ok) {
        this.debugLog(`✅ 成功添加商品（带属性): 变体${variantId}, 数量${quantity}`);
        return true;
      } else {
        this.debugLog(`❌ 添加商品失败: 变体${variantId}, 状态码: ${response.status}, 错误: ${responseText}`);
        return false;
      }
    } catch (error) {
      this.debugLog(`❌ 添加商品出错: 变体${variantId}`, error);
      return false;
    }
  }

  /**
   * 测试定制费配置（调试用）
   */
  testCustomFeeConfig() {
    this.debugLog('🧪 测试定制费配置:');
    
    // 测试所有已注册的工艺类型
    const processTypes = CraftProcessorFactory.getAllProcessTypes();
    const testQuantities = [5, 12, 50, 150, 300];
    
    for (const processType of processTypes) {
      this.debugLog(`\n🔬 测试工艺类型: ${processType}`);
      try {
        const processor = CraftProcessorFactory.getProcessor(processType);
        
        testQuantities.forEach(qty => {
          const variantId = processor.getVariantIdByQuantity(qty);
          const needsSetup = processor.needsSetupFee(qty);
          this.debugLog(`  数量 ${qty}: 变体=${variantId}, 需要Setup=${needsSetup}`);
        });
        
      } catch (error) {
        this.debugLog(`  ❌ 工艺类型 ${processType} 测试失败:`, error);
      }
    }
    
    this.debugLog('\n📋 已注册的工艺类型:', processTypes);
    this.debugLog('📋 Setup Fee配置:', SETUP_FEE_CONFIG);
  }

  /**
   * 动态添加新工艺类型的示例方法
   */
  static addNewCraftType(processType, config) {
    // 创建新的工艺处理器类
    class NewCraftProcessor extends CraftProcessor {
      constructor() {
        super({
          processType: processType,
          productHandle: config.productHandle,
          productTitle: config.productTitle,
          ranges: config.ranges
        });
      }
    }
    
    // 注册到工厂
    CraftProcessorFactory.registerProcessor(processType, () => new NewCraftProcessor());
    
    console.log(`✅ 新工艺类型 ${processType} 已注册`);
  }
}

// 将类暴露到全局作用域
window.CartCustomFeeManager = CartCustomFeeManager;